/**
 * Évaluation Live des élèves — Mode Tatami.
 *
 * Workflow mobile-first :
 *  1) Liste élèves filtrable par discipline + ceinture actuelle
 *  2) Vue éval : 3 boutons rapides (Validée / Partielle / À revoir) par technique
 *  3) Synthèse + décision : Promu / Partiel / À reprendre → update contrat
 *
 * Optimisé pour iPhone — Kru Ralph évalue avec son téléphone en main.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Medal, ArrowLeft, CheckCircle, Warning, XCircle, Trophy, Clock } from "@phosphor-icons/react";

const SCORE_BUTTONS = [
  { level: "validee", label: "Validée", value: 100, color: "emerald", icon: CheckCircle },
  { level: "partielle", label: "Partielle", value: 75, color: "amber", icon: Warning },
  { level: "a_revoir", label: "À revoir", value: 50, color: "rose", icon: XCircle },
];

const DECISION_META = {
  promu: { label: "PROMU 🎉", color: "emerald", desc: "Ceinture obtenue — le contrat sera mis à jour automatiquement" },
  partiel: { label: "PROMOTION PARTIELLE", color: "amber", desc: "À compléter sous 60 jours (notes ajoutées au contrat)" },
  a_reprendre: { label: "À REPRENDRE", color: "rose", desc: "Évaluation à refaire après plus d'entraînement" },
};

// ────────────────────────────────────────────────────────────────────────────
// VIEW 1 : SÉLECTION ÉLÈVE
// ────────────────────────────────────────────────────────────────────────────
function StudentPicker({ onPick }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ discipline: "", belt: "Blanche" });
  const [history, setHistory] = useState(null);

  useEffect(() => {
    loadStudents();
    loadHistory();
  }, [filter.discipline, filter.belt]);

  async function loadStudents() {
    setLoading(true);
    try {
      const params = {};
      if (filter.discipline) params.discipline = filter.discipline;
      if (filter.belt) params.belt = filter.belt;
      const r = await api.get("/evaluation/students", { params });
      setStudents(r.data.students || []);
    } catch (e) {
      toast.error("Erreur chargement élèves: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  async function loadHistory() {
    try {
      const r = await api.get("/evaluation/list", { params: { limit: 100 } });
      setHistory(r.data);
    } catch (e) { /* ignore */ }
  }

  async function startEval(student) {
    if (!student.suggested_target_belt) {
      toast.error("Pas de ceinture suivante — déjà à la ceinture la plus haute ?");
      return;
    }
    try {
      const r = await api.post("/evaluation/start", {
        student_contract_id: student.id,
        discipline: student.discipline,
        target_belt: student.suggested_target_belt,
      });
      onPick(r.data.evaluation);
    } catch (e) {
      toast.error("Erreur démarrage: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function resumeEval(evalId) {
    try {
      const r = await api.get(`/evaluation/${evalId}`);
      onPick(r.data.evaluation);
    } catch (e) {
      toast.error("Impossible de reprendre cette évaluation");
    }
  }

  const grouped = useMemo(() => {
    const g = {};
    students.forEach((s) => {
      const d = s.discipline || "autre";
      g[d] = g[d] || [];
      g[d].push(s);
    });
    return g;
  }, [students]);

  return (
    <div className="space-y-5" data-testid="evaluation-picker">
      {/* Header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total éval" value={history?.stats?.total || 0} color="zinc" />
        <StatCard label="Promus" value={history?.stats?.promu || 0} color="emerald" />
        <StatCard label="Partiels" value={history?.stats?.partiel || 0} color="amber" />
        <StatCard label="En cours" value={history?.stats?.en_cours || 0} color="sky" />
      </div>

      {/* Reprise si en cours */}
      {history?.stats?.en_cours > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="text-amber-300 font-semibold mb-2 flex items-center gap-2">
            <Clock size={18} /> Évaluations en cours ({history.stats.en_cours})
          </div>
          <div className="space-y-2">
            {(history.evaluations || []).filter((e) => e.decision === "en_cours").map((e) => (
              <button
                key={e.id}
                onClick={() => resumeEval(e.id)}
                data-testid={`resume-eval-${e.id}`}
                className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-amber-500/20 rounded-lg p-3 flex items-center justify-between transition"
              >
                <div>
                  <div className="text-amber-200 font-medium">{e.student_name}</div>
                  <div className="text-xs text-zinc-400">
                    {e.discipline} · {e.current_belt} → {e.target_belt} · {e.evaluated_count}/{e.total_techniques} techniques
                  </div>
                </div>
                <span className="text-amber-400 text-sm">Reprendre →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <div className="text-amber-400 font-semibold flex items-center gap-2">
          <Medal size={18} /> Sélectionner un élève à évaluer
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Discipline</label>
            <select
              data-testid="filter-discipline"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
              value={filter.discipline}
              onChange={(e) => setFilter({ ...filter, discipline: e.target.value })}
            >
              <option value="">Toutes</option>
              <option value="muay_thai">Muay Thaï</option>
              <option value="kickboxing">Kickboxing</option>
              <option value="jiu_jitsu_bresilien">BJJ</option>
              <option value="jiu_jitsu_japonais">Jiu-jitsu Can Ryu</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Ceinture actuelle</label>
            <select
              data-testid="filter-belt"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
              value={filter.belt}
              onChange={(e) => setFilter({ ...filter, belt: e.target.value })}
            >
              <option value="">Toutes</option>
              <option value="Blanche">Blanche</option>
              <option value="Blanc-Jaune">Blanc-Jaune</option>
              <option value="Jaune">Jaune</option>
              <option value="Orange">Orange</option>
              <option value="Vert">Vert</option>
              <option value="Bleu">Bleu</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste élèves groupée */}
      {loading ? (
        <div className="text-zinc-500 text-center py-8">Chargement…</div>
      ) : students.length === 0 ? (
        <div className="text-zinc-500 text-center py-8 bg-zinc-900 rounded-xl border border-zinc-800">
          Aucun élève trouvé avec ces filtres.
        </div>
      ) : (
        Object.entries(grouped).map(([disc, list]) => (
          <div key={disc} className="space-y-2">
            <div className="text-zinc-400 text-sm font-semibold uppercase tracking-wider pl-1">
              {disc} ({list.length})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map((s) => (
                <button
                  key={s.id}
                  onClick={() => startEval(s)}
                  data-testid={`start-eval-${s.id}`}
                  className="text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-amber-500/50 rounded-xl p-4 transition group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-zinc-100 font-medium truncate">{s.full_name}</div>
                      <div className="text-xs text-zinc-400 mt-1">
                        {s.age ? `${s.age} ans` : "Âge inconnu"}
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="text-zinc-400">{s.current_belt}</span>
                        <span className="text-amber-500">→</span>
                        <span className="text-amber-400 font-medium">{s.suggested_target_belt || "—"}</span>
                      </div>
                      {s.last_evaluation && (
                        <div className="text-xs text-zinc-500 mt-1.5">
                          Dernière éval : {s.last_evaluation.decision} ({s.last_evaluation.final_belt || "—"})
                        </div>
                      )}
                    </div>
                    <div className="text-amber-400 group-hover:translate-x-1 transition">→</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function StatCard({ label, value, color = "zinc" }) {
  const ring = {
    zinc: "border-zinc-700",
    emerald: "border-emerald-500/40 text-emerald-300",
    amber: "border-amber-500/40 text-amber-300",
    sky: "border-sky-500/40 text-sky-300",
    rose: "border-rose-500/40 text-rose-300",
  }[color];
  return (
    <div className={`bg-zinc-900 border ${ring} rounded-xl p-3`}>
      <div className="text-xs text-zinc-400">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// VIEW 2 : MODE TATAMI (évaluation des techniques)
// ────────────────────────────────────────────────────────────────────────────
function EvaluationRunner({ evaluation, onBack, onFinalize }) {
  const [ev, setEv] = useState(evaluation);
  const [activeIdx, setActiveIdx] = useState(() => {
    const idx = (evaluation.scores || []).findIndex((s) => s.score_level === "non_evaluee");
    return idx >= 0 ? idx : 0;
  });

  async function scoreTech(score) {
    const current = ev.scores[activeIdx];
    try {
      const r = await api.post(`/evaluation/${ev.id}/score`, {
        technique_id: current.technique_id,
        technique_name: current.technique_name,
        score_level: score.level,
      });
      // Update local
      const newScores = ev.scores.map((s, i) =>
        i === activeIdx ? { ...s, score_level: score.level, score_value: score.value, scored_at: new Date().toISOString() } : s
      );
      setEv({
        ...ev,
        scores: newScores,
        average_percent: r.data.average_percent,
        evaluated_count: r.data.evaluated_count,
        auto_decision_suggestion: r.data.auto_decision,
      });
      // Auto-advance vers la prochaine non évaluée
      const nextIdx = newScores.findIndex((s, i) => i > activeIdx && s.score_level === "non_evaluee");
      if (nextIdx >= 0) {
        setActiveIdx(nextIdx);
      } else {
        // Toutes évaluées ? sauter à la première non évaluée AVANT
        const firstNon = newScores.findIndex((s) => s.score_level === "non_evaluee");
        if (firstNon >= 0) setActiveIdx(firstNon);
      }
    } catch (e) {
      toast.error("Erreur score: " + (e?.response?.data?.detail || e.message));
    }
  }

  const total = ev.scores.length;
  const done = ev.scores.filter((s) => s.score_level !== "non_evaluee").length;
  const allDone = done === total;
  const current = ev.scores[activeIdx];

  return (
    <div className="space-y-4" data-testid="evaluation-runner">
      {/* Header sticky */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 -mx-4 px-4 py-3 mb-2">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onBack}
            data-testid="back-to-picker"
            className="flex items-center gap-1 text-zinc-400 hover:text-amber-400 text-sm"
          >
            <ArrowLeft size={16} /> Liste
          </button>
          <button
            onClick={() => onFinalize(ev)}
            disabled={done === 0}
            data-testid="goto-finalize"
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-zinc-950 font-semibold px-4 py-1.5 rounded-lg text-sm"
          >
            Finaliser ({done}/{total})
          </button>
        </div>
        <div className="text-zinc-100 font-bold text-lg leading-tight">{ev.student_name}</div>
        <div className="text-xs text-zinc-400 mt-0.5">
          {ev.discipline} · {ev.current_belt} → <span className="text-amber-400">{ev.target_belt_resolved || ev.target_belt}</span>
          {ev.student_age && ` · ${ev.student_age} ans`}
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className="bg-amber-500 h-full transition-all duration-300"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
          <span className="text-amber-400 font-bold text-sm whitespace-nowrap">
            {ev.average_percent || 0}% moy.
          </span>
        </div>
      </div>

      {/* Technique en cours */}
      {current && (
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-500/30 rounded-2xl p-5 shadow-lg" data-testid="current-technique">
          <div className="flex items-start justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-amber-400/70">
              Technique {activeIdx + 1} / {total} {current.category && `· ${current.category}`}
            </span>
            {current.score_level !== "non_evaluee" && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                current.score_level === "validee" ? "bg-emerald-500/20 text-emerald-300" :
                current.score_level === "partielle" ? "bg-amber-500/20 text-amber-300" :
                "bg-rose-500/20 text-rose-300"
              }`}>
                {current.score_level} ({current.score_value}%)
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-zinc-100 mb-4">{current.technique_name}</div>

          {/* 3 gros boutons score */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {SCORE_BUTTONS.map((s) => {
              const Icon = s.icon;
              const active = current.score_level === s.level;
              const colorMap = {
                emerald: active ? "bg-emerald-500 text-emerald-950" : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30",
                amber: active ? "bg-amber-500 text-amber-950" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/30",
                rose: active ? "bg-rose-500 text-rose-950" : "bg-rose-500/15 text-rose-300 hover:bg-rose-500/30",
              }[s.color];
              return (
                <button
                  key={s.level}
                  onClick={() => scoreTech(s)}
                  data-testid={`score-${s.level}`}
                  className={`${colorMap} font-bold py-4 rounded-xl flex flex-col items-center gap-1 transition-all active:scale-95`}
                >
                  <Icon size={28} weight={active ? "fill" : "regular"} />
                  <span className="text-sm">{s.label}</span>
                  <span className="text-xs opacity-70">{s.value}%</span>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2 mt-3">
            <button
              onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))}
              disabled={activeIdx === 0}
              className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-300 px-3 py-2 rounded-lg text-sm flex-1"
              data-testid="nav-prev"
            >
              ← Précédente
            </button>
            <button
              onClick={() => setActiveIdx(Math.min(total - 1, activeIdx + 1))}
              disabled={activeIdx === total - 1}
              className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-300 px-3 py-2 rounded-lg text-sm flex-1"
              data-testid="nav-next"
            >
              Suivante →
            </button>
          </div>
        </div>
      )}

      {/* Liste complète avec navigation rapide */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Toutes les techniques</div>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {ev.scores.map((s, i) => (
            <button
              key={s.technique_id}
              onClick={() => setActiveIdx(i)}
              data-testid={`jump-tech-${i}`}
              className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${
                i === activeIdx ? "bg-amber-500/20 text-amber-200" : "hover:bg-zinc-800 text-zinc-300"
              }`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                s.score_level === "validee" ? "bg-emerald-500" :
                s.score_level === "partielle" ? "bg-amber-500" :
                s.score_level === "a_revoir" ? "bg-rose-500" :
                "bg-zinc-700"
              }`} />
              <span className="flex-1 truncate">{s.technique_name}</span>
              {s.score_level !== "non_evaluee" && (
                <span className="text-xs text-zinc-500">{s.score_value}%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {allDone && (
        <button
          onClick={() => onFinalize(ev)}
          data-testid="finalize-cta"
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold py-4 rounded-xl text-lg flex items-center justify-center gap-2"
        >
          <Trophy size={24} weight="fill" /> Finaliser l'évaluation
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// VIEW 3 : FINALISATION
// ────────────────────────────────────────────────────────────────────────────
function EvaluationFinalize({ evaluation, onBack, onDone }) {
  const auto = evaluation.auto_decision_suggestion || (
    evaluation.average_percent >= 75 ? "promu" :
    evaluation.average_percent >= 60 ? "partiel" : "a_reprendre"
  );
  const [decision, setDecision] = useState(auto);
  const [coachNotes, setCoachNotes] = useState(evaluation.coach_notes || "");
  const [parentMsg, setParentMsg] = useState(
    decision === "promu"
      ? `Félicitations ! ${evaluation.student_name} a obtenu sa ceinture ${evaluation.target_belt_resolved || evaluation.target_belt} aujourd'hui. Bravo pour le travail accompli !`
      : decision === "partiel"
      ? `${evaluation.student_name} a démontré une bonne progression aujourd'hui. Nous avons identifié quelques techniques à consolider — nous referons un point sous 60 jours.`
      : `${evaluation.student_name} doit continuer à pratiquer avant la prochaine évaluation. On garde la motivation, on reviendra plus fort !`
  );
  const [saving, setSaving] = useState(false);

  async function finalize() {
    setSaving(true);
    try {
      const r = await api.post(`/evaluation/${evaluation.id}/finalize`, {
        decision,
        coach_notes: coachNotes,
        parent_message: parentMsg,
      });
      toast.success(
        decision === "promu"
          ? `🎉 ${evaluation.student_name} → ${evaluation.target_belt_resolved || evaluation.target_belt} !`
          : decision === "partiel"
          ? `Promotion partielle enregistrée`
          : `Évaluation à reprendre enregistrée`
      );
      onDone(r.data.evaluation);
    } catch (e) {
      toast.error("Erreur finalisation: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  const meta = DECISION_META[decision];
  const counts = {
    validee: evaluation.scores.filter((s) => s.score_level === "validee").length,
    partielle: evaluation.scores.filter((s) => s.score_level === "partielle").length,
    a_revoir: evaluation.scores.filter((s) => s.score_level === "a_revoir").length,
    non_evaluee: evaluation.scores.filter((s) => s.score_level === "non_evaluee").length,
  };

  return (
    <div className="space-y-4" data-testid="evaluation-finalize">
      <button onClick={onBack} className="flex items-center gap-1 text-zinc-400 hover:text-amber-400 text-sm">
        <ArrowLeft size={16} /> Retour à l'évaluation
      </button>

      <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-500/30 rounded-2xl p-5">
        <div className="text-xs uppercase text-amber-400/70 tracking-wider">Synthèse</div>
        <div className="text-2xl font-bold text-zinc-100 mt-1">{evaluation.student_name}</div>
        <div className="text-sm text-zinc-400 mt-0.5">
          {evaluation.discipline} · {evaluation.current_belt} → {evaluation.target_belt_resolved || evaluation.target_belt}
        </div>

        <div className="mt-4 flex items-baseline gap-3">
          <div className="text-5xl font-bold text-amber-400">{evaluation.average_percent}%</div>
          <div className="text-zinc-400 text-sm">moyenne globale</div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-4">
          <Pill color="emerald" label="Validées" v={counts.validee} />
          <Pill color="amber" label="Partielles" v={counts.partielle} />
          <Pill color="rose" label="À revoir" v={counts.a_revoir} />
          <Pill color="zinc" label="Non éval." v={counts.non_evaluee} />
        </div>
      </div>

      {/* Décision */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="text-amber-400 font-semibold mb-3">Décision finale</div>
        <div className="grid grid-cols-1 gap-2">
          {["promu", "partiel", "a_reprendre"].map((d) => {
            const m = DECISION_META[d];
            const active = decision === d;
            const colorMap = {
              emerald: active ? "bg-emerald-500/20 border-emerald-500 text-emerald-200" : "border-zinc-700 hover:border-emerald-500/50",
              amber: active ? "bg-amber-500/20 border-amber-500 text-amber-200" : "border-zinc-700 hover:border-amber-500/50",
              rose: active ? "bg-rose-500/20 border-rose-500 text-rose-200" : "border-zinc-700 hover:border-rose-500/50",
            }[m.color];
            return (
              <button
                key={d}
                onClick={() => setDecision(d)}
                data-testid={`decision-${d}`}
                className={`text-left border-2 ${colorMap} rounded-lg p-3 transition`}
              >
                <div className="font-bold">{m.label} {auto === d && <span className="text-xs opacity-70 ml-2">(suggéré auto)</span>}</div>
                <div className="text-xs opacity-80 mt-0.5">{m.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Notes coach (privé)</label>
          <textarea
            value={coachNotes}
            onChange={(e) => setCoachNotes(e.target.value)}
            data-testid="coach-notes"
            rows={3}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
            placeholder="Observations, points à travailler…"
          />
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Message parent/élève (à partager)</label>
          <textarea
            value={parentMsg}
            onChange={(e) => setParentMsg(e.target.value)}
            data-testid="parent-message"
            rows={4}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
          />
        </div>
      </div>

      <button
        onClick={finalize}
        disabled={saving}
        data-testid="confirm-finalize"
        className={`w-full bg-${meta.color}-500 hover:bg-${meta.color}-400 text-${meta.color}-950 font-bold py-4 rounded-xl text-lg disabled:opacity-50`}
        style={{
          backgroundColor: decision === "promu" ? "#10b981" : decision === "partiel" ? "#f59e0b" : "#f43f5e",
          color: "#0a0a0a",
        }}
      >
        {saving ? "Enregistrement…" : `Confirmer : ${meta.label}`}
      </button>
    </div>
  );
}

function Pill({ color, label, v }) {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300",
    amber: "bg-amber-500/15 text-amber-300",
    rose: "bg-rose-500/15 text-rose-300",
    zinc: "bg-zinc-800 text-zinc-400",
  }[color];
  return (
    <div className={`${map} rounded-lg p-2 text-center`}>
      <div className="text-lg font-bold">{v}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN
// ────────────────────────────────────────────────────────────────────────────
export default function EvaluationLiveModule() {
  const { evalId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState("picker"); // picker | runner | finalize | done
  const [activeEval, setActiveEval] = useState(null);

  useEffect(() => {
    if (evalId) {
      api.get(`/evaluation/${evalId}`).then((r) => {
        setActiveEval(r.data.evaluation);
        setView(r.data.evaluation.decision === "en_cours" ? "runner" : "done");
      }).catch(() => navigate("/app/evaluation"));
    }
  }, [evalId, navigate]);

  function handleEvaluationPicked(ev) {
    setActiveEval(ev);
    setView("runner");
  }

  function handleGoFinalize(ev) {
    setActiveEval(ev);
    setView("finalize");
  }

  function handleDone(ev) {
    setActiveEval(ev);
    setView("done");
  }

  function handleBackToPicker() {
    setActiveEval(null);
    setView("picker");
  }

  return (
    <div className="max-w-3xl mx-auto pb-12">
      <div className="mb-5">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-100 flex items-center gap-2">
          <Medal size={32} className="text-amber-400" weight="fill" />
          Évaluation Live
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Mode Tatami — sélectionne un élève, score les techniques, valide la décision.
        </p>
      </div>

      {view === "picker" && <StudentPicker onPick={handleEvaluationPicked} />}
      {view === "runner" && activeEval && (
        <EvaluationRunner
          evaluation={activeEval}
          onBack={handleBackToPicker}
          onFinalize={handleGoFinalize}
        />
      )}
      {view === "finalize" && activeEval && (
        <EvaluationFinalize
          evaluation={activeEval}
          onBack={() => setView("runner")}
          onDone={handleDone}
        />
      )}
      {view === "done" && activeEval && (
        <div className="space-y-5" data-testid="evaluation-done">
          <div className="bg-gradient-to-br from-emerald-900/40 to-zinc-900 border border-emerald-500/40 rounded-2xl p-6 text-center">
            <Trophy size={48} weight="fill" className="text-amber-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100">{activeEval.student_name}</div>
            <div className="text-sm text-zinc-400 mt-1">
              Décision : <span className="text-amber-300 font-semibold uppercase">{activeEval.decision}</span>
            </div>
            <div className="text-4xl font-bold text-amber-400 mt-3">{activeEval.average_percent}%</div>
            {activeEval.final_belt && (
              <div className="text-sm text-zinc-300 mt-2">
                Ceinture finale : <span className="text-amber-300 font-semibold">{activeEval.final_belt}</span>
              </div>
            )}
            {activeEval.contract_belt_updated && (
              <div className="text-xs text-emerald-300 mt-2">✓ Contrat élève mis à jour automatiquement</div>
            )}
          </div>
          <button
            onClick={handleBackToPicker}
            data-testid="back-after-done"
            className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold py-3 rounded-xl"
          >
            Évaluer un autre élève →
          </button>
        </div>
      )}
    </div>
  );
}
